import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { PropertyGallery } from '@/components/property/PropertyGallery';
import { PropertyGrid } from '@/components/property/PropertyGrid';
import { ContactForm } from '@/components/common/ContactForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, useBreadcrumbs } from '@/components/ui/Breadcrumb';
import { useProperty, useSimilarProperties } from '@/hooks/useProperties';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenant } from '@/hooks/useTenant';
import { formatPrice, formatArea, generateWhatsAppLink } from '@/lib/utils';
import {
  Bed, Bath, Square, MapPin, Calendar, Share2, Heart,
  Phone, Mail, MessageCircle, CheckCircle, Car, Wifi,
  Waves, TreePine, Shield, Zap
} from 'lucide-react';
import { SEO } from '@/components/shared/SEO';
import { showErrorToast } from '@/utils/toast';
import { generatePropertyStructuredData, generateBreadcrumbStructuredData } from '@/utils/structuredData';

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { property, loading, error } = useProperty(id!);
  const { properties: similarProperties, loading: similarLoading } = useSimilarProperties(id!);
  const { t } = useLanguage();
  const { config } = useTenant();

  // Show toast notification when error occurs
  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-96 bg-gray-200 rounded-xl mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-8 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-5/6" />
                </div>
              </div>
              <div className="h-96 bg-gray-200 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Propiedad no encontrada
          </h1>
          <p className="text-gray-600 mb-8">
            La propiedad que buscas no existe o ha sido removida.
          </p>
          <Button asChild>
            <Link to="/ventas">Ver Otras Propiedades</Link>
          </Button>
        </div>
      </div>
    );
  }

  const featureIcons: Record<string, any> = {
    'piscina': Waves,
    'jardín': TreePine,
    'garage': Car,
    'internet': Wifi,
    'seguridad': Shield,
    'electricidad': Zap,
  };

  const whatsappMessage = `Hola, me interesa la propiedad: ${property.title} - ${formatPrice(property.price, property.currency)}`;
  const whatsappLink = generateWhatsAppLink(config?.contact.whatsapp || config?.contact.phone || '', whatsappMessage);

  const locationText = typeof property.location === 'string' ? property.location : property.location?.city || property.location?.formattedAddress || 'Ubicación';
  const propertyDescription = `${property.title} - ${formatPrice(property.price, property.currency)} - ${property.bedrooms} dormitorios, ${property.bathrooms} baños, ${formatArea(property.area)}. ${locationText}`;

  // Get base URL for structured data
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://example.com';

  // Generate structured data
  const propertyStructuredData = generatePropertyStructuredData(property, baseUrl);

  const breadcrumbStructuredData = generateBreadcrumbStructuredData(
    [
      { name: 'Inicio', url: '/' },
      { name: property.operation === 'venta' ? 'Ventas' : 'Arriendos', url: property.operation === 'venta' ? '/ventas' : '/arriendos' },
      { name: property.title, url: `/propiedad/${id}` },
    ],
    baseUrl
  );

  // Generate breadcrumb items
  const breadcrumbItems = useBreadcrumbs([
    {
      label: property.operation === 'venta' ? 'Ventas' : 'Arriendos',
      href: property.operation === 'venta' ? '/ventas' : '/arriendos',
    },
    {
      label: property.title,
    },
  ]);

  return (
    <>
      <SEO
        title={`${property.title} - ${property.operation === 'venta' ? 'Venta' : 'Arriendo'}`}
        description={propertyDescription}
        keywords={[
          property.type,
          locationText,
          property.operation,
          'propiedad',
          'inmueble',
          'bienes raíces',
          ...property.features.slice(0, 3)
        ]}
        canonicalPath={`/propiedad/${id}`}
        ogImage={property.images[0]}
        ogType="product"
        structuredData={[propertyStructuredData, breadcrumbStructuredData]}
      />
      <div className="min-h-screen">
      {/* Gallery Section */}
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb items={breadcrumbItems} />
        <PropertyGallery
          images={property.images}
          title={property.title}
        />
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Property Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge 
                  variant={property.operation === 'venta' ? 'default' : 'secondary'}
                  className="text-sm"
                >
                  {property.operation === 'venta' ? 'VENTA' : 'ARRIENDO'}
                </Badge>
                {property.featured && (
                  <Badge variant="destructive" className="text-sm">
                    DESTACADO
                  </Badge>
                )}
                <Badge variant="outline" className="text-sm">
                  {property.status.toUpperCase()}
                </Badge>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {property.title}
              </h1>

              <div className="flex items-center text-gray-600 mb-6">
                <MapPin className="h-5 w-5 mr-2" />
                <span className="text-lg">
                  {typeof property.address === 'string'
                    ? property.address
                    : property.address?.formattedAddress || property.address?.city || 'Dirección no disponible'}
                </span>
              </div>

              <div className="text-4xl font-bold text-blue-600 mb-6">
                {formatPrice(property.price, property.currency)}
                {property.operation === 'arriendo' && <span className="text-lg text-gray-500">/mes</span>}
              </div>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 rounded-xl">
              {property.bedrooms && (
                <div className="text-center">
                  <Bed className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                  <div className="font-semibold">{property.bedrooms}</div>
                  <div className="text-sm text-gray-600">Dormitorios</div>
                </div>
              )}
              {property.bathrooms && (
                <div className="text-center">
                  <Bath className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                  <div className="font-semibold">{property.bathrooms}</div>
                  <div className="text-sm text-gray-600">Baños</div>
                </div>
              )}
              <div className="text-center">
                <Square className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                <div className="font-semibold">{formatArea(property.area)}</div>
                <div className="text-sm text-gray-600">Superficie</div>
              </div>
              {property.builtArea && (
                <div className="text-center">
                  <Square className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                  <div className="font-semibold">{formatArea(property.builtArea)}</div>
                  <div className="text-sm text-gray-600">Construidos</div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold mb-4">Descripción</h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                {property.description.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Features */}
            {property.features.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Características</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {property.features.map((feature, index) => {
                    const IconComponent = featureIcons[feature.toLowerCase()] || CheckCircle;
                    return (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                        <IconComponent className="h-5 w-5 text-green-600" />
                        <span className="text-gray-700 capitalize">{feature}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Location */}
            <div>
              <h2 className="text-2xl font-bold mb-4">Ubicación</h2>
              <div className="bg-gray-100 rounded-xl p-8 text-center">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">
                  {typeof property.address === 'string'
                    ? property.address
                    : property.address?.formattedAddress || property.address?.city || 'Dirección no disponible'}
                </p>
                <p className="text-gray-600">
                  {typeof property.location === 'string'
                    ? property.location
                    : property.location?.city || property.location?.neighborhood || property.location?.formattedAddress || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Agent Card */}
            {property.agent && (
              <div className="bg-white p-6 rounded-xl shadow-lg border">
                <h3 className="text-xl font-bold mb-4">Agente Inmobiliario</h3>
                <div className="flex items-center space-x-4 mb-4">
                  {property.agent.image && (
                    <img
                      src={property.agent.image}
                      alt={property.agent.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <div className="font-semibold text-lg">{property.agent.name}</div>
                    <div className="text-gray-600">Agente de Ventas</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Button className="w-full" asChild>
                    <a href={`tel:${property.agent.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Llamar
                    </a>
                  </Button>
                  <Button variant="outline" className="w-full" asChild>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" className="w-full" asChild>
                    <a href={`mailto:${property.agent.email}`}>
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </a>
                  </Button>
                </div>
              </div>
            )}

            {/* Contact Form */}
            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h3 className="text-xl font-bold mb-4">Solicitar Información</h3>
              <ContactForm
                propertyId={property.id}
                source="property"
              />
            </div>

            {/* Share */}
            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h3 className="text-xl font-bold mb-4">Compartir Propiedad</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartir
                </Button>
                <Button variant="outline" size="sm" className="flex-1">
                  <Heart className="h-4 w-4 mr-2" />
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Properties */}
        {similarProperties.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-8">Propiedades Similares</h2>
            <PropertyGrid
              properties={similarProperties}
              loading={similarLoading}
            />
          </div>
        )}
      </div>
    </div>
    </>
  );
}